// TradeAccounts.tsx
import React, { useMemo } from "react";
import {
    Card,
    Group,
    Badge,
    Text,
    Table,
    Stack,
    Divider,
    Progress,
    ThemeIcon,
} from "@mantine/core";
import { IconTrendingUp, IconTrendingDown } from "@tabler/icons-react";
import { KfhTradeAccount, StockBalance } from "../../types";

type Props = {
    accounts: KfhTradeAccount[];
};

const fmt = (ccy: string) => {
    const nf = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    });
    return {
        format: (value: number) => `${nf.format(value)} ${ccy}`,
    };
};

const pctFmt = new Intl.NumberFormat("en-GB", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function plColor(value: number) {
    if (value > 0) return "teal";
    if (value < 0) return "red";
    return "gray";
}

function computeHoldingMetrics(h: StockBalance) {
    const marketValue = (h.lastTradePrice ?? 0) * (h.netHoldings ?? 0);
    const unitCost =
        (h.weightedAvgCost ?? 0) > 0
            ? h.weightedAvgCost
            : (h.avgCost ?? 0) > 0
                ? h.avgCost
                : (h.weightedAvgPrice ?? 0) > 0
                    ? h.weightedAvgPrice
                    : h.avgPrice ?? 0;
    const costBasis = unitCost * (h.netHoldings ?? 0);
    const unrealized = marketValue - costBasis;
    const unrealizedPct = costBasis !== 0 ? unrealized / costBasis : 0;
    return { marketValue, unitCost, costBasis, unrealized, unrealizedPct };
}

export const TradeAccounts: React.FC<Props> = ({ accounts }) => {
    // Step 1: Filter inactive (no cash or stock)
    const active = accounts.filter((a) => a.cash_balance || (a.stock_balance && a.stock_balance.length));

    // Step 2: Group by currency and merge balances
    const grouped = useMemo(() => {
        const map = new Map<
            string,
            { currency: string; cash: number; blocked: number; stocks: StockBalance[] }
        >();

        for (const acc of active) {
            const key = acc.CURRENCY;
            if (!map.has(key)) {
                map.set(key, { currency: key, cash: 0, blocked: 0, stocks: [] });
            }
            const item = map.get(key)!;
            if (acc.cash_balance) {
                item.cash += acc.cash_balance.balance ?? 0;
                item.blocked += acc.cash_balance.blockedAmount ?? 0;
            }
            if (acc.stock_balance && acc.stock_balance.length > 0) {
                item.stocks.push(...acc.stock_balance);
            }
        }
        return Array.from(map.values());
    }, [active]);

    return (
        <Stack gap="md">
            {grouped.map((grp) => {
                const currencyFmt = fmt(grp.currency);

                // Aggregates
                let marketValue = 0;
                let costBasis = 0;
                let unrealized = 0;
                for (const h of grp.stocks) {
                    const m = computeHoldingMetrics(h);
                    marketValue += m.marketValue;
                    costBasis += m.costBasis;
                    unrealized += m.unrealized;
                }

                const availableCash = grp.cash - grp.blocked;
                const totalNAV = availableCash + marketValue;
                const unrealizedPct = costBasis !== 0 ? unrealized / costBasis : 0;

                return (
                    <Card key={grp.currency} withBorder shadow="sm" radius="lg" padding="md">
                        <Group justify="space-between" align="center">
                            <Group gap="xs">
                                <Badge color={grp.currency === "KWD" ? "blue" : "gray"}>{grp.currency}</Badge>
                                <Text fw={600}>{grp.currency === "KWD" ? "Kuwait Stock Portfolio" : "Foreign Portfolio"}</Text>
                            </Group>

                            <Group gap="sm">
                                <ThemeIcon variant="light" color={plColor(unrealized)}>
                                    {unrealized >= 0 ? <IconTrendingUp size={16} /> : <IconTrendingDown size={16} />}
                                </ThemeIcon>
                                <Text fw={600} c={plColor(unrealized)}>
                                    {currencyFmt.format(unrealized)} ({pctFmt.format(unrealizedPct)})
                                </Text>
                            </Group>
                        </Group>

                        <Divider my="sm" />

                        <Group gap="lg" wrap="wrap">
                            <Stack gap={2}>
                                <Text fz="sm" c="dimmed">
                                    Available Cash
                                </Text>
                                <Text fw={600}>{currencyFmt.format(availableCash)}</Text>
                            </Stack>

                            <Stack gap={2}>
                                <Text fz="sm" c="dimmed">
                                    Market Value
                                </Text>
                                <Text fw={600}>{currencyFmt.format(marketValue)}</Text>
                            </Stack>

                            <Stack gap={2}>
                                <Text fz="sm" c="dimmed">
                                    Cost Basis
                                </Text>
                                <Text fw={600}>{currencyFmt.format(costBasis)}</Text>
                            </Stack>

                            <Stack gap={2} style={{ minWidth: 220 }}>
                                <Text fz="sm" c="dimmed">
                                    NAV (Cash + Market)
                                </Text>
                                <Text fw={600}>{currencyFmt.format(totalNAV)}</Text>
                                {totalNAV > 0 && marketValue > 0 && (
                                    <Progress value={(marketValue / totalNAV) * 100} />
                                )}
                                <Text fz="xs" c="dimmed">
                                    Equity Exposure: {totalNAV > 0 ? pctFmt.format(marketValue / totalNAV) : "—"}
                                </Text>
                            </Stack>
                        </Group>

                        <Divider my="sm" />

                        <Text fw={600}>Holdings</Text>
                        <Table withTableBorder highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Symbol</Table.Th>
                                    <Table.Th>Exchange</Table.Th>
                                    <Table.Th ta="right">Qty</Table.Th>
                                    <Table.Th ta="right">Last</Table.Th>
                                    <Table.Th ta="right">Unit Cost</Table.Th>
                                    <Table.Th ta="right">Market Value</Table.Th>
                                    <Table.Th ta="right">Cost Basis</Table.Th>
                                    <Table.Th ta="right">Unrealized</Table.Th>
                                    <Table.Th ta="right">Unrealized %</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {grp.stocks.map((h, idx) => {
                                    const { marketValue, unitCost, costBasis, unrealized, unrealizedPct } =
                                        computeHoldingMetrics(h);
                                    return (
                                        <Table.Tr key={`${h.symbol}-${h.exchange}`}>
                                            <Table.Td>{h.symbol}</Table.Td>
                                            <Table.Td>
                                                <Badge variant="light" color="gray">
                                                    {h.exchange}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td ta="right">{h.netHoldings}</Table.Td>
                                            <Table.Td ta="right">{currencyFmt.format(h.lastTradePrice ?? 0)}</Table.Td>
                                            <Table.Td ta="right">{currencyFmt.format(unitCost)}</Table.Td>
                                            <Table.Td ta="right">{currencyFmt.format(marketValue)}</Table.Td>
                                            <Table.Td ta="right">{currencyFmt.format(costBasis)}</Table.Td>
                                            <Table.Td ta="right" c={plColor(unrealized)}>
                                                {currencyFmt.format(unrealized)}
                                            </Table.Td>
                                            <Table.Td ta="right" c={plColor(unrealized)}>
                                                {pctFmt.format(unrealizedPct || 0)}
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>

                        <Text fz="xs" c="dimmed" mt="xs">
                            Market Value = Last Trade × Holdings. Unrealized P/L vs. Cost Basis. NAV combines cash and equity positions.
                        </Text>
                    </Card>
                );
            })}
        </Stack>
    );
};
